import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import { Column } from 'react-table'
import { Button, Dropdown, message, Modal, Select } from 'antd'
import type { MenuProps } from 'antd'
import { useBackgroundNavigate } from '@shared/hooks/use-background-navigate'
import * as Icons from '@ant-design/icons'

import { nextTableColumns } from '@shared/modules/next-table/helpers/next-table-columns'
import { StopPropagation } from '@shared/components/stop-propagation'
import { localURLMaker } from '@shared/utils/url-maker'
import { useSearchParams } from '@shared/hooks/use-search-params'

import { Department } from '../types'
import { DepartmentsTableContext } from '../context'
import { departmentQueryKeys } from '../utils/department-query-keys'
import { useStatusList } from '../../status/hooks/use-list'
import { filterOption } from '@shared/modules/form/utils'
import { DepartmentsService } from '../services'

export const useDepartmentsTable = () => {
  const { handleFetch } = useContext(DepartmentsTableContext)
  const navigate = useBackgroundNavigate()
  const searchParams = useSearchParams<{ reFetchDepartmentsTable?: string }>()

  const statusCollection = useStatusList({ modelId: 25 })

  const getRowProps = useCallback(
    (id) => ({
      onDoubleClick: () => navigate(localURLMaker('/departments/:id/details', { id }), { withBackground: true }),
    }),
    [navigate],
  )

  const columns = useMemo<Column<Department>[]>(
    () => [
      {
        ...nextTableColumns.actions,
        Cell: ({ row: { original } }) => {
          const goTo = useCallback(
            (type: string, withBackground = false) => {
              navigate(localURLMaker('/departments/:id/:type', { id: original.id, type }), { withBackground })
            },
            [original.id],
          )

          const handleDelete = useCallback(() => {
            Modal.confirm({
              content: 'Şöbəni silmək istəyirsiniz?',
              okText: 'Sil',
              onOk: async () => {
                const response = await DepartmentsService.deleteById(original.id)
                if (response.status === 200) {
                  message.success(response.data)
                  navigate(localURLMaker('/departments', {}, { reFetchDepartmentsTable: true }))
                } else {
                  message.error(response.data)
                }
              },
            })
          }, [original.id])

          const menuItems: MenuProps['items'] = [
            {
              key: 'update',
              label: 'Dəyişdir',
              icon: <Icons.EditOutlined />,
              onClick: () => goTo('update', true),
            },
            {
              key: 'details',
              label: 'Ətraflı',
              icon: <Icons.FileOutlined />,
              onClick: () => goTo('details', false),
            },
            {
              key: 'delete',
              label: 'Sil',
              icon: <Icons.DeleteOutlined />,
              onClick: handleDelete,
            },
          ]

          return (
            <StopPropagation>
              <Dropdown menu={{ items: menuItems }}>
                <Button size='small' icon={<Icons.MoreOutlined />} />
              </Dropdown>
            </StopPropagation>
          )
        },
      },
      {
        ...nextTableColumns.small,
        accessor: (row) => row.id,
        id: departmentQueryKeys.id,
        Header: 'Kod',
      },
      {
        accessor: (row) => row.departmentName,
        id: departmentQueryKeys.departmentName,
        Header: 'Şöbə adı',
      },
      {
        ...nextTableColumns.large,
        accessor: (row) => row.departmentCode,
        id: departmentQueryKeys.departmentCode,
        Header: 'Şöbə kodu',
      },
      {
        ...nextTableColumns.small,
        accessor: (row) => row.warehousesCount,
        id: departmentQueryKeys.warehousesCount,
        Header: 'Anbar sayı',
        Filter: () => null,
      },
      {
        accessor: (row) => row.state.state_name,
        id: departmentQueryKeys.stateId,
        Header: 'Status',
        Filter: ({ column: { filterValue, setFilter } }) => (
          <Select showSearch filterOption={filterOption} style={{ width: '100%' }} value={filterValue} onChange={setFilter} allowClear={true}>
            {statusCollection.data?.map((status) => (
              <Select.Option key={status.id} value={status.id.toString()}>
                {status.name}
              </Select.Option>
            ))}
          </Select>
        ),
      },
      {
        ...nextTableColumns.date,
        accessor: (row) => row.createdAt,
        id: departmentQueryKeys.createdAt,
        Header: 'Yaranma tarixi',
      },
    ],
    [navigate, statusCollection.data],
  )

  useEffect(() => {
    (async () => {
      if (searchParams.searchParams.reFetchDepartmentsTable) {
        searchParams.remove.current('reFetchDepartmentsTable')
        await handleFetch()
      }
    })()
  }, [searchParams.searchParams.reFetchDepartmentsTable, searchParams.remove, handleFetch])

  return { columns, getRowProps }
}
